/**
 * The widget catalog — what each brief IS, independent of any one day's
 * data.
 *
 * Three consumers, one definition: the detail page renders `workflow` as the
 * public disclosure (design spec §8), the add route turns it into the
 * `workflow_create` instruction, and the publisher Lambda reads it through the
 * renderer bundle. The agents repo owns the prompt that actually RUNS on a
 * schedule; this is the presentation-and-disclosure copy of it. There,
 * `src/widgets/config.test.ts` pins the constraint sentences and
 * `tests/workflows/builtin-widget-workflows.test.ts` pins the ranking sentence
 * in the instruction that runs; `catalog.test.ts` pins the same ranking
 * sentence here. So the two cannot silently diverge on the part that carries
 * legal weight.
 *
 * The prompt is deliberately public (spec §6): the detail page renders it as
 * the product demonstration. Nothing sensitive belongs in a widget prompt.
 */
import type { RendererId } from './payload.ts';

export interface WidgetWorkflow {
  /** Shown on the detail page and used as the created workflow's name. */
  readonly displayName: string;
  /** One or two sentences — `workflow_create`'s `description`. */
  readonly description: string;
  /** The research instruction, rendered publicly on the detail page. */
  readonly prompt: string;
  readonly cron: string;
  readonly timeZone: string;
  /** Ordered steps, each one autonomous turn. */
  readonly steps: readonly string[];
}

export interface WidgetDefinition {
  readonly id: string;
  readonly title: string;
  readonly kicker: string;
  /**
   * What kind of research the brief is, for the card badge.
   *
   * Every stock card says GROWTH RESEARCH because that is genuinely what these
   * screens are: names ranked by growth potential, as evidenced by DISCLOSED
   * demand and capacity signals. Saying so on the card matters — a reader who
   * takes a ranked list of tickers for a buy list has misread the page, and
   * the badge is the first thing above the title.
   */
  readonly focus: string;
  readonly agent: string;
  readonly renderer: RendererId;
  readonly order: number;
  readonly workflow: WidgetWorkflow;
}

/**
 * The order the names appear in. Keep byte-identical to `RANK_BY_GROWTH` in the
 * agents repo's `packages/runtime/src/widgets/config.ts`, which is the copy
 * that runs; the reasoning — including why "breakout" is not in it — lives
 * there. Five since 2026-11-03, when the six industry briefs became one.
 */
const RANK_BY_GROWTH =
  'Order the five by growth potential, highest first — the largest and best-evidenced growth the disclosures support at the top — not by recent price momentum.';

/**
 * What these screens are FOR, said in the prompt and on the card.
 *
 * The horizon is the company's own disclosed timeline, never a prediction of
 * ours — a predicted timeline is the timing language `NO_ADVICE` forbids two
 * lines below, so the two constraints have to agree or the model gets a
 * contradiction. The risk is required for the same reason it is required in
 * the schema: a growth screen that only lists upside is not research.
 */
const GROWTH_FOCUS =
  'This is growth-potential research: for each name give the growth timeline the company itself disclosed or guided to, and one disclosed fact that cuts against the thesis.';

/**
 * The window the stock brief looks across, stated openly (owner's decision,
 * 2026-10-25). Byte-identical to the agents repo's copy.
 *
 * A named window is new: until then the prompts barred all "timing language".
 * It stays on the research side of design spec §11 by what it is anchored to —
 * catalysts the COMPANY disclosed or guided to inside the window, never a
 * forecast of ours — and `NO_ADVICE` still bars entry and exit points, price
 * targets, and any advice on when to trade. The wording is flagged for a legal
 * read in `plans/2026-10-25-combined-stock-and-ai-briefs.md` (agents repo).
 */
const GROWTH_HORIZON =
  'Look across the next three to six months, and say so: rank on growth catalysts the company itself disclosed or guided to inside that window, never on a forecast of your own.';

/**
 * Shared by every market widget. Spec §11 — enforced in the prompt itself.
 *
 * "No timing language" became "no advice on when to buy or sell" on
 * 2026-11-03, when the brief gained a disclosed horizon: the old phrase would
 * have contradicted `GROWTH_HORIZON` in the same prompt. What it always meant —
 * no market-timing calls — is now what it says.
 */
const NO_ADVICE =
  'Report observations only — no entry or exit points, no price targets, no advice on when to buy or sell.';

/**
 * 05:00 CT. The detail page publishes each cron as the disclosure, and it is
 * also the only statement of a brief's days: the infra repo builds each
 * EventBridge rule, and the publisher its staleness window, from this cron via
 * `cronRunDays` (`schedule.ts`). Changing a day here changes when it fires.
 *
 * Monday and Wednesday since 2026-09-26 (cut from daily on cost grounds); the
 * AI news brief adds Friday (2026-11-03), because a week of AI news goes stale
 * faster than a quarter's disclosures do.
 */
const MON_WED_5AM = { cron: '0 5 * * 1,3', timeZone: 'America/Chicago' } as const;
const MON_WED_FRI_5AM = { cron: '0 5 * * 1,3,5', timeZone: 'America/Chicago' } as const;

/** The steps of a ranked brief, as disclosed on its page. */
const rankedSteps = (what: string, count: string): readonly string[] => [
  `Search ${what} from the last 30 days`,
  `Rank ${count} names by growth potential, highest and best-evidenced first — not price momentum`,
  'For each, record the growth timeline the company itself disclosed',
  'For each, record one disclosed fact that cuts against the thesis',
  // Disclosed because it actually runs (agents `builtin-widget-workflows.ts`
  // step 2) and because it is the strongest thing this page can say about
  // itself: prod published 12 unreachable citations out of 53 on 2026-08-23,
  // and this is the step that exists to stop that recurring.
  'Re-open every cited document and confirm it says what the note claims',
  'Drop any source that cannot be opened or does not support its sentence',
  'Emit ranked-list JSON within the schema caps',
];

/**
 * The three widgets, in display order. What the fold must still show at each
 * viewport is `ABOVE_FOLD_COUNT` / `PHONE_ABOVE_FOLD_COUNT` below.
 *
 * Six industry stock briefs (semiconductor, hyperscaler, energy, biotech,
 * defense, space) were replaced on 2026-11-03 by ONE cross-industry brief, at
 * the owner's request: the best growth opportunities across all sectors, not
 * the best three inside each. Their pages were retired with no redirect, also
 * by decision. Weather is last, on request (2026-08-29), and the positions are
 * load-bearing (spec §5) rather than a ranking of how interesting each topic is.
 */
export const WIDGETS: readonly WidgetDefinition[] = [
  {
    id: 'stock-growth-opportunities',
    title: 'Stock Growth Opportunities',
    kicker: 'GROWTH STOCKS',
    focus: 'growth research',
    agent: 'vera',
    renderer: 'ranked-list',
    order: 1,
    workflow: {
      displayName: 'Stock Growth Opportunities',
      description:
        'A Monday and Wednesday brief ranking the five publicly traded companies, from any sector, with the best-evidenced growth opportunity over the next three to six months.',
      prompt: `Research publicly traded companies across every sector and find the five with the best growth opportunities, as evidenced by what they disclosed in the last 30 days: earnings releases and guidance, SEC filings, contract awards, backlog, capacity, and product or regulatory milestones. Weigh every sector against the others rather than taking a quota from each. ${RANK_BY_GROWTH} ${GROWTH_HORIZON} ${GROWTH_FOCUS} ${NO_ADVICE}`,
      ...MON_WED_5AM,
      steps: rankedSteps('earnings releases, filings, and guidance across every sector', 'five'),
    },
  },
  {
    id: 'ai-news',
    title: 'AI News',
    kicker: 'AI & AGENTS',
    focus: 'news summary',
    agent: 'vera',
    renderer: 'news-list',
    order: 2,
    workflow: {
      displayName: 'AI News',
      description:
        'A Monday, Wednesday and Friday brief on the most important AI news: new models, GitHub repositories, agent tools, and regulation.',
      prompt:
        'Summarise the most important artificial-intelligence news since the previous brief, looking back no more than seven days: new models drawing wide use or attention (the release, its licence, and the benchmark claims its maker published), new or trending GitHub repositories and what they do, agent harnesses and developer tools, AI regulation and policy in the US, the EU and China, and any other major AI news. Choose between three and seven items, most important first, and do not pad a quiet week. Report what each source states — no speculation, no predictions, no investment angle.',
      ...MON_WED_FRI_5AM,
      steps: [
        'Search the last seven days of model releases, trending repositories, tool launches and AI policy news',
        'Pick three to seven items, most important first — never pad a quiet week',
        "Open each item's primary source: the maker's announcement, the repository page, the regulator's text",
        'Re-open every cited document and confirm it says what the note claims',
        'Drop any source that cannot be opened or does not support its sentence',
        'Emit news-list JSON within the schema caps',
      ],
    },
  },
  {
    id: 'extreme-weather',
    title: 'Extreme Weather Today',
    kicker: 'US EXTREME WEATHER',
    focus: 'live alerts',
    agent: 'vera',
    renderer: 'alert-list',
    order: 3,
    workflow: {
      displayName: 'Extreme Weather Today',
      description:
        'A Monday and Wednesday summary of the most severe active US National Weather Service alerts.',
      prompt:
        'Summarise the three most severe active US National Weather Service alerts this morning, ranked by population exposed to the highest-severity alert in effect.',
      ...MON_WED_5AM,
      steps: [
        'Read active NWS alerts nationwide',
        'Rank three by population exposed to the highest severity in effect',
        'Emit alert-list JSON within the schema caps',
      ],
    },
  },
];

/**
 * Desktop (1440×900): every finding on the first this-many cards — one row —
 * is visible without scrolling. The action row and disclaimer may sit just
 * below the fold (spec §5).
 *
 * Until 2026-09-12 both viewports had to fit three WHOLE cards. That budget is
 * what had driven the type down to 8.5px, and readers reported the briefs too
 * small to read, worst on a phone — so legibility won and the budget moved.
 * `tests/e2e/widgets.spec.ts` measures both counts.
 */
export const ABOVE_FOLD_COUNT = 3;

/**
 * Desktop (1440×900): how many findings of EACH row-one card must clear the
 * fold (2026-11-03).
 *
 * The budget was "every finding", which a three-row card met with room to
 * spare. The stock brief ranks five since 2026-11-03, and its fourth and fifth
 * rows end at 973px of 900 at the legible type size — measured, not guessed.
 * Shrinking the type again is the trade readers rejected on 2026-09-12, so the
 * budget moved instead: the top three of every ranking are on the first
 * screen, and the rest of a list continues just below it.
 */
export const ABOVE_FOLD_FINDINGS = 3;

/** Phone (390×844): this many cards fit whole, and the next one's top edge is on screen. */
export const PHONE_ABOVE_FOLD_COUNT = 2;

/** The definition for `id`, or undefined when it is not a known widget. */
export function widgetById(id: string): WidgetDefinition | undefined {
  return WIDGETS.find((w) => w.id === id);
}

/**
 * The instruction that provisions `widget` into the caller's own account.
 *
 * Phrased as a request to the agent, not as a template to fill: workflow
 * interactions are agent tool calls (there is deliberately no create endpoint
 * — `infra/docs/api-spec.md` §3), so this is sent as the user's turn and Vera
 * answers it with `workflow_create`. Naming the steps verbatim keeps the
 * created workflow faithful to what the public detail page advertises.
 */
export function createWorkflowMessage(widget: WidgetDefinition): string {
  const steps = widget.workflow.steps.map((s, i) => `${String(i + 1)}. ${s}`).join('\n');
  return [
    `Create a workflow called "${widget.workflow.displayName}" for me.`,
    '',
    `Description: ${widget.workflow.description}`,
    '',
    `What it should do: ${widget.workflow.prompt}`,
    '',
    'Steps:',
    steps,
    '',
    "Don't schedule it — just create it. I'll decide when to run it.",
  ].join('\n');
}
